import { useEffect, useState } from "react";
import { useDownloadProgressState } from "@/zustand/DownloadProgressState";
import LoadingScreen from "@/components/shared/LoadingScreen";

const CIRCUMFERENCE = 2 * Math.PI * 18; // r=18 in the 44×44 viewBox below

type Props = {
  attachmentId: string;
  /** Height reserved for the controls bar, so the ring centers over the same
   *  stage the poster uses (not the full screen). */
  bottomInset?: number;
};

// Local disk reads resolve in tens of ms; only genuine network work lives
// long enough to cross this. Below it, the poster alone is shown — flashing a
// spinner for an already-downloaded video read as "downloading it again".
const SPINNER_GRACE_MS = 200;

/**
 * The SINGLE loading indicator over the viewer's video poster (the poster's
 * own spinner is disabled — two indicators were stacking): a determinate ring,
 * same visual family as the bubble's upload ring, once the shared download
 * (processMediaAttachment) publishes progress for this attachment — and an
 * indeterminate spinner for slower non-byte phases (decrypt, unknown size),
 * shown only after a grace period so pure cache reads never flash it. Late
 * joiners (viewer opened mid auto-download) see the current value on their
 * first render — the store is written by the single shared run, not
 * per-caller callbacks.
 */
export default function ViewerDownloadProgress({
  attachmentId,
  bottomInset = 0,
}: Props) {
  const progress = useDownloadProgressState(
    (state) => state.progress[attachmentId],
  );
  const [pastGrace, setPastGrace] = useState(false);
  // Render-phase reset (same pattern as the dialog's per-item resets): it must
  // land in the SAME commit as the new item, or a swipe paints item B's first
  // frame with item A's elapsed grace — a spurious spinner flash over a
  // disk-cached video. The effect below only (re)arms the timer.
  const [graceForId, setGraceForId] = useState(attachmentId);
  if (graceForId !== attachmentId) {
    setGraceForId(attachmentId);
    setPastGrace(false);
  }
  useEffect(() => {
    const t = setTimeout(() => setPastGrace(true), SPINNER_GRACE_MS);
    return () => clearTimeout(t);
  }, [attachmentId]);
  const stage = {
    height: `calc(100% - ${bottomInset}px)`,
  } as const;
  if (progress === undefined) {
    if (!pastGrace) {
      return null; // disk-read window: poster only, no indicator
    }
    return (
      <div
        data-testid="viewer-download-spinner"
        className="pointer-events-none absolute inset-x-0 top-0 grid place-items-center"
        style={stage}
      >
        <LoadingScreen />
      </div>
    );
  }
  return (
    <div
      data-testid="viewer-download-progress"
      className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-center"
      style={stage}
    >
      <div className="rounded-2xl flex flex-col items-center gap-2 bg-black/40 p-4">
        <svg className="size-12 -rotate-90" viewBox="0 0 44 44" fill="none">
          <circle
            cx="22"
            cy="22"
            r="18"
            stroke="white"
            strokeOpacity="0.3"
            strokeWidth="3"
          />
          <circle
            cx="22"
            cy="22"
            r="18"
            className="stroke-primary/20 dark:stroke-primary"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={CIRCUMFERENCE * (1 - progress / 100)}
            style={{ transition: "stroke-dashoffset 0.3s ease" }}
          />
        </svg>
        <span className="text-xs tabular-nums text-white/90">{progress}%</span>
      </div>
    </div>
  );
}
